import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { API, Auth } from 'aws-amplify'
import { FormattedMessage, injectIntl } from 'react-intl'
import { CSSTransition } from 'react-css-transition'
import { loadStripe } from '@stripe/stripe-js'
import { CardElement, useStripe, useElements, Elements } from '@stripe/react-stripe-js'
import TransitionStyles from '../../lib/transitionStyles'
import Button from '../Button'
import Spinner from '../Spinner'
import CartStandalonePurchase from '../CartStandalonePurchase'
import { stripeOrderCreateWithPayment, totalOrderSum } from '../../lib/standalonePurchase'
import { putStripeOrder } from '../../lib/booking'

const CheckoutForm = injectIntl(
  ({ items, skus, discountCode, couponInfo, intl, steps, setStep, userInfo }) => {
    const stripe = useStripe()
    const elements = useElements()
    const [fetching, setFetching] = useState(false)
    const [paymentErrorMsg, setPaymentErrorMsg] = useState(null)
    const { SHOPPING, COMPLETED} = steps
    const stripeCustomerId = userInfo && userInfo.stripeCustomerId

    const handleSubmit = async (event) => {
      event.preventDefault()
      
      if (!stripe || !elements) {
        // Stripe.js has not loaded yet. Make sure to disable
        // form submission until Stripe.js has loaded.
        return
      }
      setFetching(true)

      // Get a reference to a mounted CardElement. Elements knows how
      // to find your CardElement because there can only ever be one of
      // each type of element.

      try {
      
        const user = await Auth.currentAuthenticatedUser()
        const { email } = user.attributes
        const stripeOrder = await stripeOrderCreateWithPayment(items, email, couponInfo, intl, skus, discountCode)
        
        try {
          const paymentIntent = await API.post('cphill', '/stripe/proxy', {
            body: {
              endpoint: 'paymentIntents.create',
              parameters: {
                amount: totalOrderSum(items, skus, discountCode) * 100,
                currency: 'DKK',
                payment_method_types: ['card'],
                ...(stripeCustomerId && { customer: stripeCustomerId }),
                metadata: {
                  order_type: 'renewals',
                  order_id: stripeOrder.id
                }
              }
            }
          })


          // Pay order
          const cardElement = elements.getElement(CardElement)
          const paymentConfirmation = await stripe.confirmCardPayment(paymentIntent.client_secret, {
            payment_method: {
              card: cardElement,
              billing_details: {
                name: `${userInfo.firstName} ${userInfo.lastName}`,
                email: userInfo.email
              }
            },
            setup_future_usage: 'on_session'
          })
          if (paymentConfirmation && paymentConfirmation.error) {
            setPaymentErrorMsg(paymentConfirmation.error.message)
            await putStripeOrder(stripeOrder.id, { status: 'canceled' })
          } else {
            setPaymentErrorMsg(null)

            try {
              window.ga('send', 'pageview', '/renewals/completed')
            } catch (error) { /* Do nothing */ }
            setStep(COMPLETED)
          }
        } catch (error) {
          console.error(error)
          await putStripeOrder(stripeOrder.id, { status: 'canceled' })
        }
      } catch(error) {
        setPaymentErrorMsg(`Unidentified error - ${error}`)
        console.error(error)
      } finally {
        setFetching(false)
      }
    }

    return (
      <form onSubmit={handleSubmit}>
        <div className="w-5/6 lg:w-3/4 xl:w-2/3 mx-auto">
          <CSSTransition {...TransitionStyles} transitionAppear active>
            <h5 className="text-harbor-80 uppercase text-sky tracking-wide-2xl">
              <FormattedMessage id="renewals.category" defaultMessage="Renewals" />
            </h5>
            <h1 className="text-4xl border-b pb-8">
              <FormattedMessage id="renewals.payment.header" defaultMessage="Payment" />
            </h1>
            <CardElement
              className={`
                ${paymentErrorMsg && 'border-red'}
                mt-8 border rounded-full bg-white px-4 py-3 text-sm
              `}
            />
            {paymentErrorMsg && (
              <div className="text-red">
                {paymentErrorMsg}
              </div>
            )}
            <img
              src="https://www.copenhill.dk/user/themes/copenhill/images/creditcards.png?cache1"
              alt="Creditcards"
              className="h-16 -ml-2 mt-4"
            />
            
            {fetching || !stripe || !elements ? (
              <Spinner type="compact" />
              ) : (
                  <>
                    <div className="flex justify-end items-center mt-8">
                      <button onClick={() => setStep(SHOPPING)} type="button" className="focus:outline-none text-sky uppercase font-bold mr-4">
                        <FormattedMessage id="renewals.payment.back" defaultMessage="Back" />
                      </button>
                      <Button bgCss="bg-grass" disabled={!stripe}>
                        <FormattedMessage id="renewals.payment.buttonPay" defaultMessage="Pay" />
                      </Button>
                    </div>
                    <div className="mt-12">
                      <CartStandalonePurchase
                        items={items}
                        skus={skus}
                      />
                    </div>
                  </>
                )}
          </CSSTransition>
        </div>
      </form>
    )
  }
)

// Make sure to call `loadStripe` outside of a component’s render to avoid
// recreating the `Stripe` object on every render.
// eslint-disable-next-line no-undef
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY)

const PaymentStep = ({ items, skus, discountCode, couponInfo, steps, setStep, userInfo }) => (
  <Elements stripe={stripePromise}>
    <CheckoutForm 
      items={items}
      skus={skus}
      discountCode={discountCode}
      couponInfo={couponInfo}
      steps={steps}
      setStep={setStep}
      userInfo={userInfo}
    />
  </Elements>
)

const propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  skus: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  discountCode: PropTypes.string,
  couponInfo: PropTypes.shape(),
  steps: PropTypes.shape().isRequired,
  setStep: PropTypes.func.isRequired,
  userInfo: PropTypes.shape()
}

const defaultProps = {
  discountCode: null,
  couponInfo: null,
  userInfo: {}
}

CheckoutForm.propTypes = propTypes
CheckoutForm.defaultProps = defaultProps
PaymentStep.propTypes = propTypes
PaymentStep.defaultProps = defaultProps

export default PaymentStep